import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { updateUser } from '../../store/userSlice';
import { logout, refresh } from '../../store/authSlice';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import userService from '../../services/userService';
import { PermissionGroups, Permission, hasPermission } from '../../constants/permissions';

const EditUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'sub_user',
    isActive: true,
    changePassword: false,
    password: '',
    confirmPassword: '',
    permissions: {} as Record<string, boolean>,
  });
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [originalUserData, setOriginalUserData] = useState<any>(null);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return;

      // Permission check: sub_user can only edit their own profile
      if (currentUser?.role === 'sub_user' && id !== currentUser?._id) {
        setError('You do not have permission to edit other users');
        setFetchLoading(false);
        setTimeout(() => {
          navigate('/users');
        }, 2000);
        return;
      }

      try {
        setFetchLoading(true);
        const user = await userService.getById(id);
        const userData = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          phone: user.phone || '',
          role: user.role || 'sub_user',
          isActive: user.isActive !== undefined ? user.isActive : true,
          changePassword: false,
          password: '',
          confirmPassword: '',
          permissions: user.permissions || {},
        };
        setFormData(userData);
        // Store original data to compare later
        setOriginalUserData({
          permissions: user.permissions || {},
          role: user.role || 'sub_user',
          isActive: user.isActive !== undefined ? user.isActive : true,
        });
      } catch (err: any) {
        setError(err.message || 'Failed to fetch user details');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchUser();
  }, [id, currentUser, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      if (name.startsWith('perm_')) {
        // Handle permission checkbox
        const permissionKey = name.replace('perm_', '');
        setFormData({
          ...formData,
          permissions: {
            ...formData.permissions,
            [permissionKey]: checked,
          },
        });
      } else {
        setFormData({ ...formData, [name]: checked });
      }
    } else {
      const newFormData = { ...formData, [name]: value };
      // If role changes from sub_user, clear permissions
      if (name === 'role' && value !== 'sub_user') {
        newFormData.permissions = {};
      }
      setFormData(newFormData);
    }
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups({
      ...expandedGroups,
      [groupKey]: !expandedGroups[groupKey],
    });
  };

  const toggleAllInGroup = (groupKey: string, enable: boolean) => {
    const group = PermissionGroups[groupKey as keyof typeof PermissionGroups];
    const newPermissions = { ...formData.permissions };
    group.permissions.forEach((perm) => {
      newPermissions[perm.key] = enable;
    });
    setFormData({ ...formData, permissions: newPermissions });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validation
    if (formData.changePassword) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
    }

    setLoading(true);

    try {
      // Prepare update data
      const updateData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
      };

      // Sub users cannot change role or active status, so don't send these fields
      if (currentUser?.role !== 'sub_user') {
        updateData.role = formData.role;
        updateData.isActive = formData.isActive;
      }

      // Only include password if changing
      if (formData.changePassword && formData.password) {
        updateData.password = formData.password;
      }

      // Determine final permissions that will be saved
      let finalPermissions = {};
      if (formData.role === 'sub_user' && hasPermission(currentUser, Permission.USERS_MANAGE_PERMISSIONS)) {
        finalPermissions = formData.permissions;
        updateData.permissions = formData.permissions;
      } else if (formData.role !== 'sub_user') {
        // Clear permissions if role is changed from sub_user
        finalPermissions = {};
        updateData.permissions = {};
      } else {
        // Keep original permissions if no changes
        finalPermissions = originalUserData?.permissions || {};
      }

      const result = await dispatch(updateUser({ id: id!, data: updateData }));
      
      if (updateUser.fulfilled.match(result)) {
        // Check if the updated user is the currently logged-in user
        const currentUserId = currentUser?.id || (currentUser as any)?._id;
        const isCurrentUser = currentUserId === id;
        
        // Check if permissions, role, or isActive were changed
        const permissionsChanged = JSON.stringify(originalUserData?.permissions || {}) !== JSON.stringify(finalPermissions || {});
        const roleChanged = originalUserData?.role !== formData.role;
        const isActiveChanged = originalUserData?.isActive !== formData.isActive;
        
        // If current user's permissions, role, or active status changed
        if (isCurrentUser && (permissionsChanged || roleChanged || isActiveChanged)) {
          // If it's a critical change (role or isActive), logout
          if (roleChanged || isActiveChanged) {
            setSuccess(true);
            setTimeout(() => {
              dispatch(logout());
              navigate('/login', { 
                state: { 
                  message: 'Your account permissions or settings were updated. Please login again to continue.' 
                } 
              });
            }, 1500);
            return;
          }
          
          // If only permissions changed, refresh instead of logout
          if (permissionsChanged) {
            try {
              // Immediately refresh user data
              const refreshResult = await dispatch(refresh());
              if (refresh.fulfilled.match(refreshResult)) {
                console.log('✅ Permissions updated, user data refreshed');
                setSuccess(true);
                setTimeout(() => {
                  navigate('/users');
                }, 2000);
                return;
              }
            } catch (refreshErr) {
              console.error('Failed to refresh after permission update:', refreshErr);
            }
          }
        }
        
        setSuccess(true);
        // Redirect after 2 seconds
        setTimeout(() => {
          navigate('/users');
        }, 2000);
      } else {
        setError(result.payload as string || 'Failed to update user');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Users
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit User</h1>
        <p className="mt-1 text-sm text-gray-600">
          Update user information and settings
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-3xl">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-600">
              ✓ User updated successfully! Redirecting...
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="John"
                />
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  disabled
                  value={formData.email}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="+1 234 567 8900"
                />
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
                  Role *
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  value={formData.role}
                  onChange={handleChange}
                  disabled={currentUser?.role === 'sub_user'}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                >
                  <option value="admin">Admin</option>
                  <option value="buyer">Buyer</option>
                  <option value="supplier">Supplier</option>
                  <option value="sub_user">Sub User</option>
                </select>
                {currentUser?.role === 'sub_user' && (
                  <p className="mt-1 text-xs text-gray-500">You cannot change your own role</p>
                )}
              </div>

              <div className="flex items-center pt-8">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  disabled={currentUser?.role === 'sub_user'}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:cursor-not-allowed"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active User
                  {currentUser?.role === 'sub_user' && (
                    <span className="text-xs text-gray-500 ml-1">(cannot be changed)</span>
                  )}
                </label>
              </div>
            </div>
          </div>

          {/* Permissions - Only show for sub_user and if user has permission to manage */}
          {formData.role === 'sub_user' && hasPermission(currentUser, Permission.USERS_MANAGE_PERMISSIONS) && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Permissions</h3>
              <p className="text-sm text-gray-600 mb-4">
                Manage permissions for this sub user. They will only be able to access features you enable here.
              </p>
              
              <div className="space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {Object.entries(PermissionGroups).map(([groupKey, group]) => (
                  <div key={groupKey} className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0">
                    <div className="flex items-center justify-between mb-2">
                      <button
                        type="button"
                        onClick={() => toggleGroup(groupKey)}
                        className="flex items-center text-sm font-medium text-gray-900 hover:text-indigo-600"
                      >
                        {expandedGroups[groupKey] ? (
                          <ChevronUpIcon className="h-4 w-4 mr-1" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 mr-1" />
                        )}
                        {group.label}
                      </button>
                      {expandedGroups[groupKey] && (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => toggleAllInGroup(groupKey, true)}
                            className="text-xs text-indigo-600 hover:text-indigo-800"
                          >
                            Select All
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => toggleAllInGroup(groupKey, false)}
                            className="text-xs text-gray-600 hover:text-gray-800"
                          >
                            Clear All
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {expandedGroups[groupKey] && (
                      <div className="ml-5 space-y-2">
                        {group.permissions.map((perm) => (
                          <label
                            key={perm.key}
                            className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                          >
                            <input
                              type="checkbox"
                              name={`perm_${perm.key}`}
                              checked={formData.permissions[perm.key] || false}
                              onChange={handleChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Password Change */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="changePassword"
                name="changePassword"
                checked={formData.changePassword}
                onChange={handleChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="changePassword" className="ml-2 block text-sm font-medium text-gray-700">
                Change Password
              </label>
            </div>

            {formData.changePassword && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    New Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    required={formData.changePassword}
                    minLength={8}
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum 8 characters
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    required={formData.changePassword}
                    minLength={8}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/users')}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update User'}
            </button>
          </div>
        </form>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-3xl">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">ℹ️ Important Information</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Email address cannot be changed for security reasons</li>
          <li>Changing the password will require the user to login again</li>
          <li>Deactivating a user will prevent them from logging in</li>
          <li>Role changes take effect immediately</li>
        </ul>
      </div>
    </div>
  );
};

export default EditUser;